import { Moment } from 'moment'

import { Status } from 'Shared/molecules/StatusLabel/types'

import { SearchResult } from 'Core/interfaces'

import { ContractNumberAndMassGroupType, SupplierItemFormTypes } from 'Portals/Internal/constants'
import { Issue, ItemGuide, PriceType } from 'Portals/Internal/entities'

import { ServiceLocation } from './ServiceLocation'

export enum ItemSearchFields {
	searchById = 'searchById',
	searchByName = 'searchByName',
	searchByRefCode = 'searchByRefCode'
}

export enum ServiceSiteFields {
	searchById = 'searchById',
	searchByName = 'searchByName',
	searchByCompanyName = 'searchByCompanyName',
	searchByProgramName = 'searchByProgramName'
}

export enum SupplierLocationFields {
	searchById = 'searchById',
	searchByName = 'searchByName'
}

export enum ItemSearchFilterFields {
	status = 'Status',
	itemGuideId = 'ItemGuideId'
}

export enum StatusItem {
	'Active',
	'Initiated',
	'Inactive',
	'Archived',
	'Incomplete'
}

export enum TypeOptions {
	onCall = 0,
	daily = 1,
	weekly = 2,
	monthly = 3
}

export enum BillingFrequencyFields {
	searchByCode = 'searchByCode',
	searchByCustomerName = 'searchByCustomerName',
	searchByServiceSiteName = 'searchByServiceSiteName'
}

export enum DependentItemSearchFields {
	searchById = 'searchById',
	searchByName = 'searchByName',
	searchByPrice = 'searchByPrice',
	searchByServiceSiteId = 'searchByServiceSiteId',
	searchByServiceSiteName = 'searchByServiceSiteName'
}

export enum ItemHashNames {
	supplierItem = 'supplierItem',
	itemPrice = 'itemPrice'
}

export declare namespace Item {
	interface Basic extends Entity {
		currentSupplier: string | null
		serviceSite: ServiceLocation.AddressEntity | null
		itemRefCode: string | null
		customBillingFrequencyCode: Entity | null
		status: Status
		lastServiceDate: string | null
	}

	namespace Exception {
		interface Basic extends Issue.IssueItem {}
		namespace Form {
			interface Edit extends Issue.Form.Edit {}
		}
		namespace Request {
			interface Edit {
				issueId?: number
				assignToId?: BasicNumber
				workflowFlag?: BasicString
				comment: BasicString
			}
		}
	}

	interface Detailed extends Entity {
		serviceSite: ServiceLocation.AddressEntity
		creationDate: string
		createdBy: string
		chargeTypeCode: Entity
		productCategory: Entity
		productType: Entity
		product: Entity
		productState: Entity
		businessType: Entity
		container: Entity
		overrideBackupRequired: boolean
		isWeightRequired: boolean
		bpmpDisplay: boolean
		isMobileItem: boolean
		status: StatusItem
		estimatedWeightPerUOM: number
		managementDescription: string
		guide: ItemGuide.Full | null
		isTurkish: boolean
		isSchedulingUsed: boolean
		isSchedulingAvailable: boolean
		customerSpecificInformations: {
			itemRefCode: BasicString
			initialApproveDate: BasicString
			itemNameNativeLanguage: BasicString
			description: BasicString
			customer: Entity | null
			treatmentCategory: CodeEntity | null
			accountCategory: CodeEntity | null
			historicalDataTrackingEndDate: BasicString
			serviceSiteProgram: Entity | null
			customerBillFreq: Entity | null
			companyCategory: CodeEntity | null
			wastestream: Entity | null
			supplierAccountNumber: BasicString
			specialHandlingInstructions: BasicString
			alwaysDataTrackingOnly: boolean
			allowCustomerToOrder: boolean
			reconcilingInvoiceOnly: boolean
			hazardous90DaysNotification: boolean
		}
	}

	namespace Validation {
		interface Basic {
			errors: Entity[]
			warnings: Entity[]
		}
	}

	type DropdownNames =
		| 'chargeTypeCodeId'
		| 'productCategoryId'
		| 'productTypeId'
		| 'productId'
		| 'productStateId'
		| 'businessTypeId'

	type ToggleNames = 'isWeightRequired' | 'bpmpDisplay' | 'isMobileItem' | 'isSchedulingUsed'

	type toggleNamesMap = {
		[key in ToggleNames]: boolean
	}

	type DropdownNamesForGuide = Exclude<DropdownNames, 'businessTypeId'>

	type DropdownNamesMap = {
		[key in DropdownNames]: number | null
	}

	type Guide = {
		[key in DropdownNamesForGuide]: number | null
	}

	namespace Form {
		interface GeneralFiled {
			name: string
			serviceSiteId: NodeLabelValue | null
		}

		type BasicForm = DropdownNamesMap & toggleNamesMap & GeneralFiled

		type AddItem = DropdownNamesMap &
			toggleNamesMap &
			GeneralFiled & {
				itemGuideId?: string | null
			}

		interface Edit extends Omit<AddItem, 'itemGuideId'> {
			creationDate: string
			createdBy: string
			containerId: number | null
			overrideBackupRequired: boolean
			status: StatusItem | null
			estimatedWeightPerUOM: number | null
			managementDescription: string | null

			customerId: LabelValue<number> | null
			customerBillFreqCodeId: LabelValue<number> | null
			treatmentCategoryId: LabelValue<number> | null
			accountCategoryId: LabelValue<number> | null
			companyCategoryId: LabelValue<number> | null
			wastestreamId: BasicNumber
			serviceSiteProgramId: LabelValue<number> | null
			historicalDataTrackingEndDate: Moment | null
			initialApproveDate: BasicString
			itemRefCode: BasicString
			itemNameNativeLanguage: BasicString
			description: BasicString
			supplierAccountNumber: BasicString
			specialHandlingInstructions: BasicString
			alwaysDataTrackingOnly: boolean
			allowCustomerToOrder: boolean
			reconcilingInvoiceOnly: boolean
			hazardous90DaysNotification: boolean
		}

		type AddFromTemplate = DropdownNamesMap &
			toggleNamesMap &
			GeneralFiled & {
				itemTemplate?: number | null
				company?: LabelValue | null
			}

		interface ItemTemplate {
			name: string
			serviceSite: Entity
			chargeTypeCode: Entity
			productCategory: Entity
			productType: Entity
			product: Entity
			productState: Entity
		}

		interface AddTreatmentMethod {
			treatmentMethodId: number | null
			materialQuantityPercent: number | null
		}
	}

	namespace Request {
		interface Generic {
			name: BasicString
			serviceSiteId: BasicNumber
			chargeTypeCodeId: BasicNumber
			productCategoryId: BasicNumber
			productTypeId: BasicNumber
			productId: BasicNumber
			productStateId: BasicNumber
			businessTypeId: BasicNumber
			isWeightRequired: boolean
			bpmpDisplay: boolean
			isMobileItem: boolean
		}

		interface Add extends Generic {}

		interface Edit extends Generic {
			containerId: BasicNumber
			isSchedulingUsed: boolean
			overrideBackupRequired: boolean
			status: StatusItem | null
			estimatedWeightPerUOM: BasicNumber
			managementDescription: BasicString

			itemRefCode: BasicString
			itemNameNativeLanguage: BasicString
			description: BasicString
			supplierAccountNumber: BasicString
			specialHandlingInstructions: BasicString
			historicalDataTrackingEndDate: BasicString
			alwaysDataTrackingOnly: boolean
			allowCustomerToOrder: boolean
			reconcilingInvoiceOnly: boolean
			hazardous90DaysNotification: boolean
			customerId: BasicNumber
			customerBillFreqCodeId: BasicNumber
			treatmentCategoryId: BasicNumber
			accountCategoryId: BasicNumber
			companyCategoryId: BasicNumber
			wastestreamId: BasicNumber
			serviceSiteProgramId: BasicNumber
		}
	}

	interface ItemBilling {
		entityName: string
		count: number
	}

	namespace Price {
		interface Basic {
			id: number
			effectiveStartDate: string
			price: number | null
			currency: string
			currencyId: number | null
			unitOfMeasure: string
			unitOfMeasureId: number | null
			orderingOfMeasure: string
			orderingUnitOfMeasureId: number | null
			scale: string
			scaleId: number | null
			variablePrice: string | null
			variablePriceId: number | null
			formula: string | null
			minimumCharge: number | null
			minimumUnitOfMeasure: string
			minimumChargeUnitOfMeasureId: number | null
			isUsedByInvoice: boolean
			hasItemSiblings: boolean
		}

		namespace Form {
			interface Data
				extends Pick<
					Basic,
					| 'unitOfMeasureId'
					| 'orderingUnitOfMeasureId'
					| 'currencyId'
					| 'price'
					| 'minimumChargeUnitOfMeasureId'
					| 'minimumCharge'
					| 'formula'
				> {
				scale: LabelValue<number> | null
				effectiveStartDate: Moment | null
				variablePrice: LabelValue<number> | null
			}

			interface DataRequest
				extends Pick<
					Basic,
					| 'unitOfMeasureId'
					| 'orderingUnitOfMeasureId'
					| 'currencyId'
					| 'price'
					| 'minimumChargeUnitOfMeasureId'
					| 'minimumCharge'
					| 'formula'
					| 'scaleId'
					| 'variablePriceId'
				> {
				effectiveStartDate: string | null
			}
		}
	}

	namespace SupplierItem {
		interface Coast {
			id: number
			effectiveStartDate: string | null
			effectiveEndDate: string | null
			chargeTypeCode: string | null
			chargeName: string | null
			chargeTypeCodeId: number | null
			cost: number | null
			minimumCharge: number | null
			minimumChargeUnitOfMeasure: CodeEntity | null
			isRequiredForCustomerItem: boolean
			quantityCodeId: string | null
			currency: string | null
			currencyId: number | null
			unitOfMeasure: string | null
			unitOfMeasureId: number | null
			isRebate: boolean
			formula: string | null
			variablePrice: Entity | null
			parent: { id: number; chargeName: string; itemId: number } | null
			isUsedByInvoice: boolean
		}

		interface SupplierLocationContractItem {
			id: number
			supplierLocationContractId: number | null
			supplierTermsAndConditionsContractId: number | null
			isActive: boolean
			supplierItemId: number | null
		}

		interface Basic {
			id: number
			supplierLocation: Entity
			billingSupplierLocation: Entity
			isActive: boolean
			isPrimaryChoice: boolean
			supplierItemCosts: Coast[]
			fulfillmentType: Entity | null
			effectiveStartDate: string | null
			referenceCode: string | null
			referenceName: string | null
			referenceDescription: string | null
			supplierLocationContractItems: SupplierLocationContractItem[]
		}

		interface BasicForTable extends Basic, Omit<Coast, 'id'> {
			supplierItemCostId?: number
			rowId: string
			shouldHideGeneralFields?: boolean
			startDateService: string | null
		}

		interface SupplierItemCostsForm
			extends Pick<
				BasicForTable,
				| 'chargeName'
				| 'chargeTypeCodeId'
				| 'quantityCodeId'
				| 'currencyId'
				| 'unitOfMeasureId'
				| 'isRebate'
				| 'cost'
				| 'minimumCharge'
				| 'isRequiredForCustomerItem'
				| 'formula'
				| 'isUsedByInvoice'
			> {
			id: number | null
			effectiveStartDate: Moment | null
			effectiveEndDate: Moment | null
			variablePrice: LabelValue<number> | null
			parent: LabelValue<number> | null
			type: SupplierItemFormTypes
			minimumChargeUnitOfMeasure: BasicNumber
		}

		interface Form
			extends Pick<
				BasicForTable,
				| 'isActive'
				| 'isPrimaryChoice'
				| 'referenceCode'
				| 'referenceName'
				| 'referenceDescription'
			> {
			fulfillmentTypeId: number | null
			supplierLocation: LabelValue<number> | null
			billingSupplierLocation: LabelValue<number> | null
			startDateService: Moment | null
			contractNumbersAndMsas:
				| (LabelValue<number> & { group: ContractNumberAndMassGroupType })
				| null
			supplierItemCosts: SupplierItemCostsForm[]
		}

		interface DataRequest
			extends Pick<
				BasicForTable,
				| 'isActive'
				| 'isPrimaryChoice'
				| 'referenceCode'
				| 'referenceName'
				| 'referenceDescription'
			> {
			fulfillmentTypeId: number | null
			supplierLocationId: number | null
			billingSupplierLocationId: number | null
			effectiveStartDate: string | null
			itemId: number | null
			supplierLocationContractItems: Array<
				Pick<
					SupplierLocationContractItem,
					| 'supplierLocationContractId'
					| 'supplierTermsAndConditionsContractId'
					| 'isActive'
				>
			>
			supplierItemCosts: Array<
				Pick<
					Coast,
					| 'chargeName'
					| 'chargeTypeCodeId'
					| 'quantityCodeId'
					| 'effectiveStartDate'
					| 'effectiveEndDate'
					| 'currencyId'
					| 'unitOfMeasureId'
					| 'isRebate'
					| 'cost'
					| 'formula'
				> & {
					variablePriceId: number | null
					parentId: number | null
					minimumCharge: BasicNumber
					minimumChargeUnitOfMeasureId: BasicNumber
					isRequiredForCustomerItem: boolean
				}
			>
		}
		namespace Search {
			namespace ParentCost {
				interface Result {
					id: number
					itemId: number
					chargeName: BasicString
					effectiveStartDate: BasicString
					effectiveEndDate: BasicString
					supplierLocation: Entity | null
				}
			}
		}
	}

	namespace TreatmentMethod {
		interface Basic {
			id: number
			treatmentMethod: string | null
			materialQuantityPercent: number | null
		}

		interface BasicResponse extends Omit<Basic, 'treatmentMethod'> {
			treatmentMethod: Entity
		}

		interface MaterialQuantityPercent {
			materialQuantityPercent: number
		}
	}

	namespace SalesforceTracking {
		interface Basic {
			id: number
			proposalDate: string | null
			approvalDate: string | null
			endSavingsDate: string | null
			salesforceId: string | null
			previousUnitPrice: number | null
			treatmentMethod: Entity | null
			unrealized: boolean
			isActive: boolean
		}

		interface KPITracking
			extends Omit<
				Basic,
				'treatmentMethod' | 'id' | 'proposalDate' | 'approvalDate' | 'endSavingsDate'
			> {
			savingTypes: LabelValue<number>[]
			proposalDate: Moment | string | null
			approvalDate: Moment | string | null
			endSavingsDate: Moment | string | null
			previousTreatmentMethodId: number | null
			excludeReasonId: number | null
			endExclusionDate: string | null
			firstServiceDate: string | null
			excludeFromSpend: boolean
			notes: string | null
			formulaId: number | null
		}

		interface AddKPITrackingResponse
			extends Omit<
				KPITracking,
				'savingTypes' | 'previousTreatmentMethodId' | 'excludeReasonId' | 'formulaId'
			> {
			id: number
			savingTypes: Entity[]
			previousTreatmentMethod: Entity
			excludeReason: Entity
			formula: Entity
		}

		namespace Form {
			interface Add
				extends Omit<
					KPITracking,
					| 'savingTypes'
					| 'proposalDate'
					| 'approvalDate'
					| 'firstServiceDate'
					| 'endSavingsDate'
				> {
				savingTypeIds: number[]
				proposalDate: string | null
				approvalDate: string | null
				endSavingsDate: string | null
			}
		}
	}

	namespace Prebill {
		interface Basic {
			isPrebill: boolean
			serviceDateCode: NameCode | null
			quantity: number | null
			quantityCode: NameCode | null
			expectSupplierInvoice: boolean
		}

		interface FormData {
			isPrebill: boolean
			serviceDateCode: string | null
			quantity: number | null
			quantityCode: string | null
			expectSupplierInvoice: boolean
		}
	}

	namespace ScheduledService {
		interface Basic {
			id: number
			service: string | null
			isActive: boolean
			numberOfContainers: number | null
			serviceStartDate: string | null
			createdOn: string | null
		}

		interface Schedule {
			$type: number
			EveryDays?: number | null
			EveryWeeks?: number | null
			EveryMonths?: number | null
			ServiceOccursPerWeek?: number | null
			ServiceOccursPerMonth?: number | null
		}

		interface ResponseSchedule extends Omit<Schedule, '$type'> {
			Type: number
		}

		interface Detailed
			extends Pick<Basic, 'numberOfContainers'>,
				Pick<
					Form.Add,
					| 'weeklySunday'
					| 'weeklyMonday'
					| 'weeklyTuesday'
					| 'weeklyWednesday'
					| 'weeklyThursday'
					| 'weeklyFriday'
					| 'weeklySaturday'
					| 'numberOfDailyServices'
				> {
			schedule: ResponseSchedule
			startDateOfService: string | null
		}

		namespace Form {
			interface Add extends Pick<Basic, 'numberOfContainers'> {
				type: number
				EveryDays?: number | null
				EveryWeeks?: number | null
				ServiceOccursPerWeek?: number | null
				EveryMonths?: number | null
				ServiceOccursPerMonth?: number | null
				weeklySunday: boolean
				weeklyMonday: boolean
				weeklyTuesday: boolean
				weeklyWednesday: boolean
				weeklyThursday: boolean
				weeklyFriday: boolean
				weeklySaturday: boolean
				numberOfDailyServices: number | null
				startDateOfService: Moment | null
			}
		}

		interface Request extends Omit<Detailed, 'schedule'> {
			schedule: Schedule
		}
	}

	namespace DependentItem {
		interface Basic extends Entity {
			itemId: number
			isActive: boolean
			isAutoCharge: boolean
			dependentItemId: number
		}

		interface AddDependentItemResponse {
			id: number
			dependentItem: Entity<string>
			quantityCode: NameCode
			isAutoCharge: boolean
		}

		namespace Form {
			interface FormData {
				dependentItem: LabelValue<string>
				quantityCodeId: string
				isAutoCharge: boolean
			}

			interface Add extends Omit<FormData, 'dependentItem'> {
				dependentItemId: number
			}
		}
	}

	namespace ParentItem {
		interface Basic extends Entity {
			itemId: number | null
			serviceSite: Entity | null
		}
	}

	namespace Search {
		interface Result extends SearchResult {}

		interface BasicResult {
			[ItemSearchFields.searchById]: Entity[]
			[ItemSearchFields.searchByName]: Entity[]
			[ItemSearchFields.searchByRefCode]: IdCode[]
		}

		interface ServiceSiteResult {
			[ServiceSiteFields.searchById]: Entity[]
			[ServiceSiteFields.searchByName]: string[]
			[ServiceSiteFields.searchByCompanyName]: string[]
		}

		interface SupplierLocationResult {
			[ServiceSiteFields.searchById]: number[]
			[ServiceSiteFields.searchByName]: string[]
		}

		interface BillingFrequencySearchResult {
			[BillingFrequencyFields.searchByCode]: Entity[]
			[BillingFrequencyFields.searchByCustomerName]: Array<Entity & { customerName: string }>
			[BillingFrequencyFields.searchByServiceSiteName]: Array<
				Entity & { serviceSiteName: string }
			>
		}

		interface SearchByServiceSite extends Entity {
			serviceSite: Entity
		}

		interface SearchByItemPrice extends Entity {
			price: number
		}

		interface DependentItemSearchResult {
			[DependentItemSearchFields.searchById]: Entity[]
			[DependentItemSearchFields.searchByName]: Entity[]
			[DependentItemSearchFields.searchByPrice]: SearchByItemPrice[]
			[DependentItemSearchFields.searchByServiceSiteId]: SearchByServiceSite[]
			[DependentItemSearchFields.searchByServiceSiteName]: SearchByServiceSite[]
		}
	}

	namespace MassEdit {
		namespace Form {
			interface Data {
				name: string
				itemRefCode: string | null
				customBillingFrequencyCode: LabelValue<number> | null
				status: StatusItem | null
			}
			interface DataRequest {
				name: {
					hasValue: boolean
					value?: string
				}
				itemRefCode: {
					hasValue: boolean
					value?: string
				}
				customBillingFrequencyCode: {
					hasValue: boolean
					value?: number | null
				}
				status: {
					hasValue: boolean
					value?: number
				}
			}
		}
	}
	namespace ServiceRequest {
		interface ScalePrices {
			highValue: number
			id: number
			isDefault: boolean
			lowValue: number
			name: string
			unit: number
		}
		interface ItemPrice {
			id: number
			effectiveStartDate: BasicString
			price: BasicNumber
			currency: BasicString
			currencyId: BasicNumber
			unitOfMeasure: BasicString
			unitOfMeasureId: BasicNumber
			orderingOfMeasure: BasicString
			orderingUnitOfMeasureId: BasicNumber
			scale: BasicString
			scaleId: BasicNumber
			variablePrice: BasicString
			variablePriceId: BasicNumber
			formula: BasicString
			minimumCharge: BasicNumber
			minimumUnitOfMeasure: BasicString
			minimumChargeUnitOfMeasureId: BasicNumber
			isUsedByInvoice: boolean
			createDate: BasicString
			hasItemSiblings: boolean
			scalePrices: ScalePrices[] | null
		}

		interface SupplierCost {
			id: number
			supplierItemId: number
			chargeName: string
			chargeTypeCodeId: number
			chargeTypeCode: BasicString
			quantityCodeId: number
			effectiveStartDate: BasicString
			effectiveEndDate: BasicString
			currencyId: number
			currency: string
			unitOfMeasureId: number
			unitOfMeasure: string
			isRebate: boolean
			cost: number
			formula: BasicString
			priceType: PriceType | null
			variablePrice: {
				id: number
				name: string
				description: string
				isActive: boolean
				beginPriceRange: number
				endPriceRange: number
			} | null
			parent: {
				id: number
				itemId: number
				chargeName: string
			} | null
			isUsedByInvoice: boolean
			minimumCharge: BasicNumber
			minimumChargeUnitOfMeasure: CodeEntity | null
			minimumChargeUnitOfMeasureId: BasicNumber
			isRequiredForCustomerItem: boolean
		}
		interface CalculationData {
			id: number
			itemPrice: ItemPrice
			supplierCost: SupplierCost
		}
	}
}
